import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api, trackPageview } from "../lib/api";
import { openSubscribe } from "../components/SubscribeModalHost";
import { HeroCarousel } from "../components/HeroCarousel";
import { HeroCTAStrip } from "../components/HeroCTAStrip";
import { SpotlightSection } from "../components/SpotlightSection";
import { IslandEssentials } from "../components/IslandEssentials";
import { WhatsAppCTA } from "../components/Sidebar";
import { PushOptIn } from "../components/PushOptIn";
import {
  LeaderboardAd,
  RectangleAd,
  SidebarTallAd,
  SidebarStickyAd,
  SponsoredCategoryLabel,
} from "../components/AdSlot";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { LangContext } from "../lib/LangContext";
import { tIsland, useReactiveLang} from "../lib/i18n";
import { ISLANDS, CATEGORY_COLORS, TEAL } from "../lib/constants";

// Papiamento/Papiamentu Headlines Hub — displays the ORIGINAL source
// headline exactly as published (zero AI rewriting). Aruba/Curaçao readers
// see the spelling and vocabulary from their own island's publications
// naturally preserved.
//
// Feature parity with the main HomePage (2026-02 audit): the hub now
// ships the full revenue architecture — HeroCarousel, IslandEssentials
// strip (with sponsored slots), SpotlightSection, and every ad slot
// (leaderboard / rectangle / sidebar-tall / sidebar-sticky / sponsored
// category label). The whole tree is wrapped in a LangContext.Provider
// value="pap" so shared child components render their PA copy from
// lib/i18n.js's `pap:` block instead of falling back to English.
//
// St. Maarten is intentionally excluded — its local languages are
// EN/NL/FR, Papiamento/Papiamentu is not part of that island's
// linguistic landscape.
const HUB_ISLAND_SLUGS = ["all", "aruba", "curacao", "bonaire"];
const ISLAND_TABS = ISLANDS.filter((i) => HUB_ISLAND_SLUGS.includes(i.slug));

// Format the ISO published_at as a short "3h ago" or "Feb 15" style label.
const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    if (isNaN(t)) return "";
    const diffMin = Math.max(0, Math.floor((Date.now() - t) / 60000));
    if (diffMin < 60) return `${diffMin}m`;
    if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h`;
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const HeadlineCard = ({ item, testIndex }) => {
  const color = CATEGORY_COLORS[item.category] || TEAL;
  const [imgFailed, setImgFailed] = useState(false);
  const hasImage = !!item.image_url && !imgFailed;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-2xl border border-[#EEEEEE] bg-white hover:border-[#0AADA0] hover:shadow-md transition-all overflow-hidden"
      data-testid={`papiamento-card-${testIndex}`}
    >
      {/* Thumbnail — colored gradient fallback with a diagonal-stripe
         overlay so a missing/broken image never leaves a broken-icon
         on the card. */}
      <div
        className="relative w-full"
        style={{ height: 160 }}
        data-testid={`papiamento-card-${testIndex}-thumb`}
      >
        {hasImage ? (
          <img
            src={item.image_url}
            alt=""
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${color} 0%, ${color}cc 60%, ${color}99 100%)`,
            }}
            data-testid={`papiamento-card-${testIndex}-thumb-fallback`}
          >
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, rgba(255,255,255,.18) 0 2px, transparent 2px 22px)",
              }}
            />
          </div>
        )}
      </div>
      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-2.5">
          {item.source_logo ? (
            <img
              src={item.source_logo}
              alt=""
              className="w-5 h-5 rounded-sm object-cover shrink-0"
              loading="lazy"
            />
          ) : (
            <span className="w-5 h-5 rounded-sm bg-[#F5F5F5] shrink-0" />
          )}
          <span className="text-[11px] text-[#6B7280] font-medium truncate">
            {item.source_name}
          </span>
          <span className="text-[#D0D5DD] text-[11px]">·</span>
          <span
            className="text-[10px] px-2 py-0.5 rounded-full font-medium tracking-wide uppercase"
            style={{ backgroundColor: `${color}15`, color }}
          >
            {item.category || "News"}
          </span>
          <span className="ml-auto text-[11px] text-[#9CA3AF] shrink-0">
            {formatWhen(item.published_at)}
          </span>
        </div>
        <h3
          className="font-serif-display text-[17px] sm:text-[18px] leading-snug text-[#1A2E35] group-hover:text-[#0AADA0] transition-colors"
          data-testid={`papiamento-card-${testIndex}-headline`}
          lang="pap"
        >
          {item.original_title}
        </h3>
        <div className="flex items-center gap-1 mt-3 text-[11px] text-[#0AADA0] opacity-0 group-hover:opacity-100 transition-opacity">
          <ExternalLink size={12} />
          <span>{item.source_name}</span>
        </div>
      </div>
    </a>
  );
};

// Adapt a PA-hub headline item to the article shape HeroCarousel expects.
// Notably: PA items don't have `ai_summary` — the carousel will render an
// empty subtitle slot in that case, which is preferable to auto-generating
// or translating a summary (violates the "raw source, zero rewriting"
// editorial rule).
const paHeroFromItem = (item) => ({
  id: item.id,
  title: item.original_title,
  image_url: item.image_url,
  url: item.url,
  category: item.category || "News",
  island: item.island,
  source_name: item.source_name,
  published_at: item.published_at,
  ai_summary: "",
  slug: "",
});

export default function PapiamentoPage() {
  // __TOGGLE_HOOK__: self-contained reactive lang state for the mini-header toggle.
  const [__toggleLang, __setToggleLang] = useReactiveLang();

  const [island, setIsland] = useState("all");
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [ads, setAds] = useState({});

  useEffect(() => {
    document.title = "Papiamento / Papiamentu Headlines — IslandsDaily";
    window.scrollTo({ top: 0, behavior: "instant" });
    trackPageview("/papiamento");
    api
      .get("/papiamento/coverage")
      .then((r) => {
        const map = {};
        (r?.data?.islands || []).forEach((i) => {
          map[i.slug] = i.has_coverage;
        });
        setCoverage(map);
      })
      .catch(() => setCoverage({}));
  }, []);

  // Auto-open the SubscribeModal when landing via ?subscribe=1 — WhatsApp
  // digest "forward-to-a-friend" links land here too if the recipient
  // happens to hit the /papiamento hub.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("subscribe") === "1") {
        openSubscribe("digest_forward");
        params.delete("subscribe");
        const q = params.toString();
        window.history.replaceState(
          {}, "",
          window.location.pathname + (q ? `?${q}` : "") + window.location.hash
        );
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Headlines + ads load together per island filter change.
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    const params = { limit: 60 };
    if (island && island !== "all") params.island = island;
    const adParams = island !== "all" ? { island } : {};
    Promise.all([
      api.get("/articles/original-headlines", { params })
        .then((r) => { if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []); })
        .catch((e) => { if (!cancelled) { setError(e?.message || "load_error"); setItems([]); } }),
      api.get("/ads", { params: adParams })
        .then((r) => { if (!cancelled) setAds(r.data || {}); })
        .catch(() => { if (!cancelled) setAds({}); }),
    ]);
    return () => { cancelled = true; };
  }, [island]);

  // Top 5 items => Hero carousel slides. Skip when we have zero items so
  // the carousel doesn't render an empty section.
  const heroArticles = useMemo(() => {
    if (!items || items.length === 0) return [];
    return items.slice(0, 5).map(paHeroFromItem);
  }, [items]);

  // Orthography selection — Aruba uses "Papiamento" (etymological: c, cc,
  // uu, plain vowels), Curaçao/Bonaire use "Papiamentu" (phonetic: k, s,
  // í/á/ü). Same language, two standardized spellings — Aruban readers
  // notice the mismatch if we serve them the Curaçao variant. See the
  // pap_aw block in lib/i18n.js for the full transformation. "All Islands"
  // defaults to pap_aw because Aruba is the primary market.
  const paLangVariant = (island === "curacao" || island === "bonaire")
    ? "pap"
    : "pap_aw";

  // Broadcast the current PA variant so components mounted outside this
  // LangContext.Provider (e.g. FloatingSubscribeCTA at the app root) can
  // render in the correct Papiamento/Papiamentu variant instead of falling
  // back to the site-wide EN/NL/ES toggle. Kept in sessionStorage (not
  // localStorage) so it doesn't leak into other tabs / non-PA pages, and
  // scoped-cleared on unmount. Same-tab consumers listen for the
  // "ir_pa_variant_changed" custom event.
  useEffect(() => {
    try {
      sessionStorage.setItem("ir_pa_variant", paLangVariant);
      window.dispatchEvent(new Event("ir_pa_variant_changed"));
    } catch (_) { /* sessionStorage may be blocked in private mode */ }
    return () => {
      try {
        sessionStorage.removeItem("ir_pa_variant");
        window.dispatchEvent(new Event("ir_pa_variant_changed"));
      } catch (_) { /* ignore */ }
    };
  }, [paLangVariant]);

  return (
    <LangContext.Provider value={paLangVariant}>
      <div className="min-h-screen bg-[#FAFAFA]" data-testid="papiamento-page">
        {/* Sticky header — kept minimal (no full app Header) so PA readers
           don't see EN/NL/ES language toggles that would send them off
           the hub. Nav includes Home + Obituaries so PA users can reach
           the obituaries page (multi-lang, already exists). */}
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
            <Link
              to="/"
              className="font-serif-display text-[18px] leading-none"
              data-testid="papiamento-home-link"
            >
              <span className="text-[#1A1A1A]">Islands</span>
              <span className="text-[#0AADA0]">Daily</span>
            </Link>
            <span className="text-[#D0D5DD]">·</span>
            <span className="text-[12px] text-[#6B7280] font-medium">
              Papiamento / Papiamentu
            </span>
            <nav className="ml-auto flex items-center gap-4 text-[13px]">
              <Link
                to="/"
                className="text-[#1A2E35] hover:text-[#0AADA0]"
                data-testid="papiamento-nav-home"
              >
                Home
              </Link>
              <Link
                to={`/obituaries?lang=${paLangVariant}`}
                className="text-[#1A2E35] hover:text-[#0AADA0]"
                data-testid="papiamento-nav-obituaries"
              >
                Obituarionan
              </Link>
              <Link
                to="/carnival"
                className="text-[#1A2E35] hover:text-[#0AADA0]"
                data-testid="papiamento-nav-carnival"
              >
                {paLangVariant === "pap_aw" ? "Carnaval 2027" : "Karnaval 2027"}
              </Link>
              <button
                type="button"
                onClick={() => openSubscribe("papiamento_header")}
                className="px-3 py-1.5 rounded-full bg-[#0AADA0] text-white text-[12px] font-medium hover:bg-[#089589] transition-colors"
                data-testid="papiamento-subscribe-btn"
              >
                {paLangVariant === "pap_aw" ? "WhatsApp Resumen" : "WhatsApp Resúmen"}
              </button>
              {/* __LANG_TOGGLE__ */}
              <LanguageToggle lang={__toggleLang} onChange={__setToggleLang} />
            </nav>
          </div>
        </div>

        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14"
          data-testid="papiamento-hero"
        >
          <h1 className="font-serif-display text-[32px] sm:text-[42px] leading-tight text-[#1A2E35]">
            Papiamento / Papiamentu
          </h1>
          {/* Hero description — variant-aware. Kept inline (not in i18n.js)
             because this text is PA-only and specific to this hub. The
             Aruban version drops "original" ("Klik ta habri e articulo
             riba un tab nobo") and uses "riba" instead of "na" for the
             preposition (per 2026-02-23 orthography audit — this is
             grammar/preposition, not a spelling swap). */}
          <p className="text-[14px] sm:text-[15px] text-[#6B7280] mt-3 max-w-2xl">
            {paLangVariant === "pap_aw"
              ? "Titularnan original for di cada isla — directamente for di e fuente, sin traduccion, sin re-escritura. Click ta habri e articulo riba un tab nobo."
              : "Titularnan original for di cada isla — directamente for di e fuente, sin traducción, sin re-escritura. Klik ta habri e artículo original na un tab nobo."}
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8">
          <div
            className="flex flex-wrap items-center gap-2"
            role="tablist"
            data-testid="papiamento-island-filter"
          >
            {ISLAND_TABS.map((i) => {
              const active = island === i.slug;
              return (
                <button
                  key={i.slug}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setIsland(i.slug)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] transition-colors border ${
                    active
                      ? "bg-[#0AADA0] text-white border-[#0AADA0]"
                      : "bg-white text-[#1A2E35] border-[#EEEEEE] hover:border-[#0AADA0]"
                  }`}
                  data-testid={`papiamento-island-${i.slug}`}
                >
                  <span aria-hidden="true">{i.flag}</span>
                  <span>{i.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <main
          className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10"
          data-testid="papiamento-main"
        >
          {/* HERO CAROUSEL — rotating top-5 PA headlines. `externalLinkOnly`
             makes the "Read Story" CTA link straight to the publisher
             instead of a synthetic /article/:id detail page (which
             doesn't exist for PA hub items). */}
          {heroArticles.length > 0 && (
            <HeroCarousel articles={heroArticles} externalLinkOnly />
          )}

          {/* Sourcing-gap notice — rendered in the space the carousel
             would occupy for islands where we have zero Papiamentu-native
             publishers onboarded (currently Bonaire). Not an error state;
             a friendly heads-up that mirrors the fuller sourcing-gap card
             further down the feed. Uses `pap` orthography because this
             banner only appears on Curaçao/Bonaire tabs (Aruba always has
             coverage; "All Islands" merges Aruba's items so it's never
             empty). */}
          {heroArticles.length === 0 &&
            island !== "all" &&
            coverage &&
            coverage[island] === false && (
              <div
                className="mb-6 rounded-2xl border border-[#F0E5D0] bg-[#FFFBF3] px-5 py-4 sm:px-6 sm:py-5"
                data-testid="papiamento-hero-sourcing-notice"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#B87333]/15 text-[#B87333] text-[11px] font-bold shrink-0"
                    aria-hidden="true"
                  >
                    i
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#B87333] font-semibold mb-1">
                      Nota
                    </div>
                    <p
                      className="text-[14px] text-[#1A2E35] leading-snug"
                      lang="pap"
                    >
                      Ainda no tin fuente Papiamentu na{" "}
                      {tIsland("pap", island)}. Wak abou pa mas detaye.
                    </p>
                  </div>
                </div>
              </div>
            )}

          <div className="mb-6" data-testid="hero-cta-strip-wrap">
            <HeroCTAStrip source="hero_strip" testid="hero-cta-strip" />
          </div>

          {/* Spotlight (Business / Tourism) — shares the same lang wiring
             via LangContext so its heading + labels render in the
             correct Papiamento variant (pap_aw for Aruba/All-Islands,
             pap for Curaçao/Bonaire). Sponsored slot flows through the
             `ads` prop. */}
          <SpotlightSection island={island} lang={paLangVariant} ads={ads} externalLinkOnly />

          {/* Island Essentials strip — includes the sponsored card
             placements (this is part of the existing revenue
             architecture, per Feb-2026 parity audit). */}
          <IslandEssentials island={island} />

          <div className="mb-8">
            <LeaderboardAd ad={ads.leaderboard} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <h2
                  className="font-serif-display text-2xl text-[#1A1A1A] flex items-center gap-2 flex-wrap"
                  data-testid="feed-heading"
                >
                  <span lang="pap">Titularnan original</span>
                  {/* Sponsored category label — only appears if admin
                     has sold this hub's category slot. Same slot type
                     as HomePage; unlocks a new revenue lane for PA. */}
                  {ads.sponsored_category && ads.sponsored_category["News"] && (
                    <SponsoredCategoryLabel ad={ads.sponsored_category["News"]} />
                  )}
                </h2>
                {items !== null && items.length > 0 && (
                  <span className="text-xs text-[#6B7280]">
                    {items.length} titularnan
                  </span>
                )}
              </div>

              {items === null && (
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  data-testid="papiamento-loading"
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-28 rounded-2xl bg-[#F0F0F0] animate-pulse"
                    />
                  ))}
                </div>
              )}
              {items !== null && items.length === 0 && !error && (
                (() => {
                  const isGap =
                    island !== "all" &&
                    coverage &&
                    coverage[island] === false;
                  if (isGap) {
                    return (
                      <div
                        className="max-w-xl mx-auto text-center py-14 px-6 rounded-2xl border border-[#F0E5D0] bg-[#FFFBF3]"
                        data-testid="papiamento-sourcing-gap"
                      >
                        <div className="text-[13px] uppercase tracking-widest text-[#B87333] font-semibold mb-3">
                          Sourcing gap
                        </div>
                        <h3 className="font-serif-display text-[20px] text-[#1A2E35] mb-3">
                          Ainda no tin fuente Papiamentu pa e isla aki
                        </h3>
                        <p className="text-[13px] text-[#6B7280] leading-relaxed">
                          We haven't onboarded a Papiamentu-language news
                          source for this island yet. Rather than mixing in
                          Dutch- or English-language headlines, this tab
                          stays empty until we add a proper Papiamentu
                          outlet. Suggestions welcome — email{" "}
                          <a
                            href="mailto:hello@islandsdaily.com"
                            className="text-[#0AADA0] underline"
                          >
                            hello@islandsdaily.com
                          </a>
                          .
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div
                      className="text-center py-16 text-[#6B7280] text-[14px]"
                      data-testid="papiamento-empty"
                    >
                      No hay titulares originales por el momento — bin trobe
                      mas laat.
                    </div>
                  );
                })()
              )}
              {items !== null && items.length > 0 && (
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5"
                  data-testid="papiamento-grid"
                >
                  {items.map((item, idx) => (
                    <HeadlineCard
                      key={item.id || idx}
                      item={item}
                      testIndex={idx}
                    />
                  ))}
                </div>
              )}
              {error && (
                <div
                  className="text-center py-8 text-[#E63946] text-[13px]"
                  data-testid="papiamento-error"
                >
                  Un error a socede — proba refresha e página.
                </div>
              )}
            </div>

            {/* Sidebar — mirrors HomePage's revenue lanes: WhatsAppCTA,
               PushOptIn, RectangleAd, SidebarTallAd, SidebarStickyAd.
               All of these ad slots are already sellable via the
               existing admin bookings; wiring them here means every ad
               inventory unit that exists on the main site is also
               sellable against the PA hub. */}
            <aside className="space-y-6" data-testid="papiamento-sidebar">
              <RectangleAd ad={ads.rectangle} />
              <WhatsAppCTA />
              <PushOptIn islandSlug={island} />
              <SidebarTallAd ad={ads.sidebar_tall} />
              <SidebarStickyAd ad={ads.sidebar_sticky} />
            </aside>
          </div>
        </main>

        <Footer onSubscribe={() => openSubscribe("papiamento_footer")} />
      </div>
    </LangContext.Provider>
  );
}
